import type { ChangeEvent, Dispatch, FC, SetStateAction } from 'react'
import { useEffect, useMemo, useState } from 'react'
import {
  CwsCheckbox,
  CwsDivider,
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
  CwsInput,
  CwsLabel,
  CwsTextarea,
} from '@components/cws'
import { useTranslate } from '@hooks/useTranslation'
import { validations } from '@utils/order'
import { useRouter } from 'next/router'
import { useAppState } from '@context/state'

export type ShareForm = {
  email: string
  message: string
  copyEmail?: string
}

interface SendEmailProps {
  shareForm: ShareForm
  setShareForm: Dispatch<SetStateAction<ShareForm>>
}

const MAX_MESSAGE = 500

const SendEmail: FC<SendEmailProps> = ({ setShareForm, shareForm }) => {
  const { translate } = useTranslate()
  const { locale } = useRouter()

  const {
    state: { customer },
  } = useAppState()

  const ownEmail = useMemo(() => (customer && customer.emailAddr ? customer.emailAddr : ''), [customer])

  const charsLeft = MAX_MESSAGE - shareForm.message.length

  const changeHandler = (field: keyof ShareForm) => (value: string) => {
    setShareForm((prevState) => ({ ...prevState, [field]: value }))
  }

  const [bccChecked, setBccChecked] = useState<boolean>(false)

  useEffect(() => {
    if (!bccChecked) {
      setShareForm((prevState) => ({ ...prevState, copyEmail: undefined }))
    } else {
      setShareForm((prevState) => ({ ...prevState, copyEmail: ownEmail || '' }))
    }
  }, [bccChecked, ownEmail, setShareForm])

  const helperText =
    charsLeft === MAX_MESSAGE
      ? translate('w2.helper.text.optional')
      : translate('reviews.adding.characters.left', { chars: charsLeft })

  return (
    <CwsGrid width="fluid" className="cws-mb-10">
      <CwsGridRow>
        <CwsGridCol lg="6" col="12">
          <CwsLabel>{`${translate('wishlist.share.email.sendto')}: `}</CwsLabel>
          <CwsInput
            type="email"
            value={shareForm.email}
            locale={locale}
            onChange={changeHandler('email')}
            invalid={shareForm.email.length && !shareForm.email.match(validations.email)}
          />
        </CwsGridCol>
        <CwsGridCol col="12">
          <CwsLabel>{`${translate('wishlist.share.email.msg')}: `}</CwsLabel>
          <CwsTextarea
            id="message-text"
            locale={locale}
            optional
            value={shareForm.message}
            onChange={changeHandler('message')}
            maxLength={MAX_MESSAGE}
            helperText={helperText}
          />
        </CwsGridCol>
      </CwsGridRow>
      <CwsDivider variant="dashed" color="medium-grey" spacing="l" />
      <CwsCheckbox
        checked={bccChecked}
        id="should-send-copy"
        inline
        onChange={(value: boolean) => setBccChecked(value)}
      >
        {translate('wishlist.share.email.sendcopy')}
      </CwsCheckbox>
      {bccChecked && (
        <CwsGridRow>
          <CwsGridCol lg="6" col="12" className="cws-mt-20">
            <CwsLabel>{`${translate('wishlist.share.your.email')}: `}</CwsLabel>
            <CwsInput
              id={'copyOfEmail'}
              optional
              type="email"
              locale={locale}
              value={shareForm?.copyEmail}
              onChange={changeHandler('copyEmail')}
              invalid={shareForm.copyEmail?.length && !shareForm.copyEmail?.match(validations.email)}
            />
          </CwsGridCol>
        </CwsGridRow>
      )}
    </CwsGrid>
  )
}

export default SendEmail
